import { isValid } from 'date-fns';

import { formatInAppTz } from '@/utils/spain-time';

/**
 * Formats a date into its "DD/MM/YYYY" and "HH:MM:SS" parts, as the Madrid
 * wall clock (`@/utils/spain-time`) whatever the browser's timezone.
 *
 * - Parameters:
 *   - `date`: A `Date` or a date string, or `null`.
 *
 * - Returns:
 *   - `{ datePart, timePart }`, both `null` when there is no date.
 */
const formatDate = (date: string | Date | null) => {
  if (!date) {
    return {
      datePart: null,
      timePart: null,
    };
  }

  return {
    datePart: formatInAppTz(date, 'dd/MM/yyyy'),
    timePart: formatInAppTz(date, 'HH:mm:ss'),
  };
};

export type TDateFormat =
  | 'yyyy-MM-dd' /* 2025-02-19 */
  | 'yyyy-MM-dd HH:mm' /* 2025-02-19 15:30 */
  | 'yyyy-MM-dd HH:mm:ss' /* 2025-02-19 15:30:45 */
  | 'dd/MM/yyyy' /* 19/02/2025 */
  | 'dd/MM/yyyy HH:mm' /* 19/02/2025 15:30*/
  | 'dd/MM/yyyy HH:mm:ss' /* 19/02/2025 15:30:00*/
  | 'MM/dd/yyyy' /* 02/19/2025 */
  | 'HH:mm' /* 15:30 */
  | 'mm:ss' /* 30:45 */
  | 'HH:mm:ss' /* 15:30:45 */
  | 'dd-MM-yyyy' /* 19-02-2025 */
  | 'dd MMM yyyy' /* 19 Feb 2025 */
  | 'MMMM do, yyyy'; /* February 19th, 2025 */

type TFormateDateCustomProps = {
  input: Date | string;
  format: TDateFormat;
};

/** Formats a date with one of the product's patterns, as the Madrid wall clock. */
function formatDateCustom({ input, format }: TFormateDateCustomProps): string {
  const dateObj = input instanceof Date ? input : new Date(input);

  if (!isValid(dateObj)) {
    throw new Error('Fecha inválida');
  }

  return formatInAppTz(dateObj, format);
}

function getDurationInSecondsBetweenStartDateAndEndDate(startDate: Date, endDate: Date): number {
  const start = new Date(startDate);
  const end = new Date(endDate);

  const durationInSeconds = Math.ceil((end.getTime() - start.getTime()) / 1000);

  return durationInSeconds;
}

export { formatDate, formatDateCustom, getDurationInSecondsBetweenStartDateAndEndDate };
